---
title: Browser preview
lede: Open the pages a session serves, inside the app, and mark up what is wrong for the agent.
order: 20
part: Check the result
---

The Browser surface (`⌘4`) shows the pages your session's projects serve, from inside the sandbox. You use it to check the work and to point the agent at what should change.

![The browser bar](media/browser-toolbar.webp)

### The bar

One row: **Back**, **Forward**, **Reload** (**Stop** while a page loads), then the address, the note tools, **Viewport** and **More** (`…`).

- The **project** heads the address. Click it to list the workspace's projects that serve a page; click one, or press its digit `1`–`9` while the list is open. Drag rows, or press `⌥⇧↑` / `⌥⇧↓`, to reorder them for the whole workspace.
- The address shows the page as the project sees it inside the sandbox. Type a port or a path to go elsewhere.
- The key at the end of the address opens **Logins** (see below).
- **More** holds **Open in window** (for a second screen), **Open in browser** (your own browser) and **Developer tools**.

### What it can reach

Only the ports this session publishes. The address bar cannot open the internet, a port of your computer, or another session.

A port added to a project after the session started is greyed in the list. On a session on this computer it is published the next time the project starts; a micro-VM needs a new session.

### Viewport

**Viewport** sets the page's width: **Fill the pane**, **Tablet — 834 px** or **Mobile — 390 px**. It changes the layout width, not the zoom, so you see what the page does at that size.

### Noting an element

**Note an element** turns the pointer into a picker. Click something on the page and write the note. The agent receives the element's selector and visible text, so it knows which button you mean. **Stop** or `Esc` ends the pointing.

### Drawing on the page

![Drawing on the page](media/browser-annotate.webp)

**Draw and note** lays a canvas over the page. Mark what is wrong, then **Add note**: the agent receives a screenshot of the page with your marks on it.

![The marker](media/browser-marker.webp)

| Tool | How to use it |
|---|---|
| **Draw freehand** | Drag |
| **Rectangle** | Click one corner, then the other |
| **Zone** | Click each corner; click the first point again to close it |

Six inks (red, orange, yellow, green, blue, purple) keep several remarks apart. **Clear** wipes the marks, **Done** puts the pointer back.

### Sending notes

Notes wait until you send them. The bar counts them; click the note button beside the count to review the list and **Take back** one you do not want. **Send to Agent** types every waiting note of the session into the agent's terminal as one message, with the notes left on [diff lines](/changes):

```
[3] http://localhost:5173/products — served by storefront — element: #stock-pill ("6 left")
    -> This should say "In stock" above the threshold, not a number.

[4] http://localhost:5173/products — served by storefront — annotated screenshot: /workspace/.uploads/<id>/preview-<t>.png
    -> The pill overlaps the price at this width; see the red marks.
```

Notes on [log lines](/projects) are the exception: they are sent at once.

### Logins

![Logins for this page](media/browser-logins.webp)

The key in the address (**Logins for this page**) lists the workspace's saved logins for this address. **Fill** types one into the page's form; the password is typed by this computer and never enters the sandbox. **Add a login** takes a **Username** and a **Password**; the bin on a row forgets it.

When you sign in by hand, the bar offers **Remember** that login; press **Save** or **Not now**. Logins are kept in the OS keychain, per workspace. See [Vault](/vault).

### When nothing loads

![The preview error](media/browser-error.webp)

**This page did not load** says why in a sentence, then shows the error and address, with **Retry**. The usual causes:

- *No process is listening on port 5173 inside the VM.* The server is still starting, has exited, or listens on another port; its log in the dock says which. Listening on `127.0.0.1` or `localhost` is fine: Airlock relays it.
- *The session's VM is not taking connections on port 5173.* It is parked, stopped or still booting. Open the session to wake it.
- *Port 5173 accepted the connection and closed it without answering*, on a session on this computer. The process is still starting, or it does not serve HTTP there.

**No port published by this session** means no project declares a port: give one in the workspace settings.
